'use client'

import * as React from 'react'

import { cn } from '../../../lib/utils'
import Icon, { type iconTypes } from '../Icon'
import s from '../../styles/animations.module.css'
import {
  Select as SelectP,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './primative'
import MetafieldLabel from '../MetafieldLabel'
import { FieldChange } from '../FieldChange'
import { OptionTag } from '../OptionTag'

export type SelectItem = {
  value: string | null
  label: string
  icon?: iconTypes
  iconUrl?: string | null
  suffix?: string
  description?: string
  isDisabled?: boolean
  /** When any item has a color, the select renders its options as tags. */
  color?: string | null
}

export interface SelectProps
  extends Omit<React.InputHTMLAttributes<HTMLButtonElement>, 'value'> {
  className?: string
  triggerClassName?: string
  placeholder?: string
  labelClassName?: string
  leftIcon?: iconTypes
  rightIcon?: iconTypes
  isLoading?: boolean
  isSkeleton?: boolean
  isDisabled?: boolean
  isValidated?: boolean
  label?: string
  errorMessage?: string
  helpText?: string
  infoText?: string
  id: string
  title?: string
  textAlignment?: 'left' | 'center' | 'right'
  value?: string | null
  originalValue?: string | null
  /** Spreadsheet cells: shown when the cell is disabled and has no value. */
  emptyLabel?: string
  items: SelectItem[]
  handleChange?: (
    v: string,
    obj?: Record<string, string>,
  ) => void | Promise<void>
  handleOnOpen?: () => void | Promise<void>
  handleOnClose?: () => void | Promise<void>
  handleKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  metafield?: {
    label: string
    href: string
  }
}

function renderSelectItemIcon(item: Pick<SelectItem, 'icon' | 'iconUrl'>) {
  if (item.iconUrl) {
    return (
      <img
        src={item.iconUrl}
        alt=""
        className="h-4 w-4 rounded-sm object-contain shrink-0"
      />
    )
  }

  if (item.icon) {
    return <Icon name={item.icon} className="w-4 h-4 shrink-0" />
  }

  return null
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      id,
      className,
      triggerClassName,
      label,
      labelClassName,
      leftIcon,
      rightIcon,
      isLoading,
      isSkeleton,
      isValidated,
      textAlignment,
      isDisabled,
      errorMessage,
      title,
      helpText,
      infoText,
      value = '',
      originalValue,
      items,
      placeholder,
      handleChange,
      handleKeyDown,
      handleOnOpen,
      handleOnClose,
      metafield,
      ...props
    },
    ref,
  ) => {
    if (isSkeleton) {
      return (
        <div className="w-full">
          <label
            className={cn({
              'text-destructive opacity-100 leading-tight w-full': errorMessage,
            })}
          >
            {label}
            <div className="h-9 w-full bg-secondary animate-pulse rounded-md border border-input text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:outline-none focus-within:ring-1 focus-within:ring-ring disabled:cursor-not-allowed disabled:opacity-50"></div>
          </label>
        </div>
      )
    }

    // Helper function to normalize values for comparison
    const normalizeValue = (val: string | null | undefined): string => {
      if (val === null || val === undefined || val === '') {
        return '( None )'
      }
      // Look up the option and return its label
      const option = items.find((item) => item.value === val)
      return option ? option.label : String(val)
    }

    // Check if we should show FieldChange component
    const shouldShowFieldChange =
      originalValue !== undefined &&
      normalizeValue(originalValue) !== normalizeValue(value)

    const selectedItem = items.find((item) => item.value === value)
    const showTags = items.some((item) => item.color)
    const triggerIcon = showTags ? null : selectedItem ? (
      renderSelectItemIcon(selectedItem)
    ) : leftIcon ? (
      <Icon name={leftIcon} className="w-4 h-4" />
    ) : null

    return (
      <div className="bg-transparent">
        <label
          className={cn(
            {
              'text-destructive opacity-100 leading-tight w-full': errorMessage,
            },
            className,
          )}
          htmlFor={id}
        >
          {label && (
            <div
              className={cn(
                'flex justify-between items-center text-muted-foreground mb-1 h-5',
                className,
                labelClassName,
              )}
            >
              <span className="inline-flex items-center gap-1">
                {label}
                {infoText ? (
                  <span
                    title={infoText}
                    aria-label={infoText}
                    className="inline-flex text-muted-foreground"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <circle cx="8" cy="8" r="6.25" />
                      <path d="M8 7.25v4" strokeLinecap="round" />
                      <circle
                        cx="8"
                        cy="4.75"
                        r="0.6"
                        fill="currentColor"
                        stroke="none"
                      />
                    </svg>
                  </span>
                ) : null}
              </span>
              <div className="flex items-center gap-2">
                {shouldShowFieldChange && (
                  <FieldChange
                    oldValue={normalizeValue(originalValue)}
                    newValue={normalizeValue(value)}
                    size="sm"
                    handleRevert={() => {
                      if (handleChange) {
                        handleChange(originalValue || '')
                      }
                    }}
                  />
                )}
                {metafield && <MetafieldLabel {...metafield} />}
              </div>
            </div>
          )}
          <SelectP
            disabled={isDisabled}
            value={value ?? ''}
            onOpenChange={(value) => {
              if (value) {
                handleOnOpen && handleOnOpen()
              } else {
                handleOnClose && handleOnClose()
              }
            }}
            onValueChange={(value) => handleChange?.(value)}
          >
            <SelectTrigger
              title={title || `select-${id}`}
              onKeyDown={(e) => {
                handleKeyDown?.(e)
              }}
              ref={ref}
              className={triggerClassName}
            >
              <div className="flex min-w-0 items-center gap-2">
                {triggerIcon && !isLoading && triggerIcon}
                {isLoading && (
                  <Icon
                    name="LoaderCircle"
                    height={15}
                    width={15}
                    className="animate-spin opacity-50"
                  />
                )}
                <SelectValue placeholder={placeholder}>
                  {selectedItem && showTags ? (
                    <OptionTag
                      label={selectedItem.label}
                      color={selectedItem.color}
                      icon={selectedItem.icon}
                    />
                  ) : (
                    selectedItem?.label ?? null
                  )}
                </SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              {items
                .filter((i) => i.value !== '' && i.value !== null)
                .map((i, index) => (
                  <SelectItem
                    disabled={i.isDisabled}
                    title={
                      title
                        ? `${title}-option-${i.value}`
                        : `select-${id}-option-${i.value}`
                    }
                    key={`select-${index}`}
                    className="h-9"
                    value={i.value!}
                    textValue={i.label}
                  >
                    <div className="flex gap-2 items-center">
                      <span className="flex gap-2 items-center">
                        {!showTags && renderSelectItemIcon(i)}
                        <div className="flex flex-col items-start">
                          {showTags ? (
                            <OptionTag
                              label={i.label}
                              color={i.color}
                              icon={i.icon}
                            />
                          ) : (
                            <span
                              className={cn(
                                'leading-tight',
                                i.description ? 'text-xs' : 'text-sm',
                              )}
                            >
                              {i.label}
                            </span>
                          )}
                          {i.description && (
                            <span className="text-muted-foreground text-2xs leading-tight">
                              {i.description}
                            </span>
                          )}
                        </div>
                      </span>
                      {i.suffix && (
                        <span className="text-muted-foreground">
                          {i.suffix}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </SelectP>
          {helpText && (
            <div className="text-muted-foreground normal-case text-xs mt-2">
              {helpText}
            </div>
          )}
          {errorMessage && (
            <div
              className={cn(
                'text-destructive text-xs mt-2',
                s.smallTransitionIn,
              )}
            >
              {errorMessage}
            </div>
          )}
        </label>
      </div>
    )
  },
)

Select.displayName = 'Select'

export { Select }
